<template>
  <div v-if="showStatsMenu">
    <v-app-bar height="76" flat color="black" class="league-bar" :style="{ '--blob-color': leagueHex }">
      <v-container max-width="1300">
        <div class="title-section">
          <div class="ios-row-icon league-banner-icon league-logo-link" @click="goToHome">
            <img :src="getLeagueImage(season?.imageUrl)" alt="Hlavní obrázek" />
          </div>
          <div class="title-wrap league-logo-link" @click="goToHome">
            <span class="league-name">{{ season?.title }}</span>
            <span class="season-year" v-if="season?.year">{{ season.year }}</span>
            <span class="season-year" v-else-if="season?.isAcademy">{{ $t('typeAcademy') }}</span>
          </div>

          <!-- Academy tabs -->
          <template v-if="season?.isAcademy">
            <v-tabs v-model="activeSection" class="px-0 d-none d-sm-flex" :slider-color="leagueHex">
              <v-tab :to="`/leagues/${season.urlTitle}/academy/schedule`" key="schedule" class="my-tab"
                :text="$t('schedule')"></v-tab>
              <v-tab :to="`/leagues/${season.urlTitle}/academy/articles`" key="articles" class="my-tab"
                :text="$t('articleEditor.articles')"></v-tab>
              <v-tab v-if="isLeagueAdmin" :to="`/leagues/${season.urlTitle}/academy/treasury`" key="treasury" class="my-tab"
                :text="$t('treasury')"></v-tab>
            </v-tabs>
          </template>

          <!-- Regular league tabs -->
          <template v-else>
            <v-tabs v-model="activeSection" class="px-0 d-none d-sm-flex" :slider-color="leagueHex">
              <v-tab :to="`/leagues/${season.urlTitle}/${season.year}/schedule`" key="schedule" class="my-tab"
                :text="$t('schedule')"></v-tab>
              <v-tab :to="`/leagues/${season.urlTitle}/${season.year}/standings`" key="standings" class="my-tab"
                :text="$t('standings')"></v-tab>
              <v-tab :to="`/leagues/${season.urlTitle}/${season.year}/statistics`" key="stats" class="my-tab"
                :text="$t('stats')"></v-tab>
              <v-tab :to="`/leagues/${season.urlTitle}/${season.year}/playoff`" key="playoff" class="my-tab"
                :text="$t('playoff.title')"></v-tab>
              <v-tab :to="`/leagues/${season.urlTitle}/${season.year}/articles`" key="articles" class="my-tab"
                :text="$t('articleEditor.articles')"></v-tab>
              <v-tab disabled :to="`/leagues/${season.urlTitle}/${season.year}/info`" key="info" class="my-tab"
                :text="$t('info')"></v-tab>
              <v-tab disabled :to="`/leagues/${season.urlTitle}/${season.year}/seasons`" key="seasons" class="my-tab"
                :text="$t('seasons')"></v-tab>
              <v-tab v-if="isLeagueAdmin" :to="`/leagues/${season.urlTitle}/${season.year}/treasury`" key="treasury" class="my-tab"
                :text="$t('treasury')"></v-tab>
            </v-tabs>
          </template>
        </div>
      </v-container>
    </v-app-bar>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';

import { useRoute, useRouter } from 'vue-router';
import { getLeagueImage } from '@/utils/imageUtils';
import { useUserStore } from '@/stores/user/userStore';
import axiosInstance from '@/services/axiosInstance';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';

const route = useRoute();
const router = useRouter();
const userStore = useUserStore();

const goToHome = () => {
  if (props.season?.isAcademy && props.season?.urlTitle) {
    router.push(`/leagues/${props.season.urlTitle}/academy/schedule`)
  } else if (props.season?.urlTitle && props.season?.year) {
    router.push(`/leagues/${props.season.urlTitle}/${props.season.year}`)
  }
};

const leagueAdmins = ref<string[]>([]);
const isLeagueAdmin = computed(() => userStore.hasRole(leagueAdmins.value));

const props = defineProps({
  season: {
    type: Object,
    required: true,
    default: () => ({
      title: 'hockeybook.online',
      urlTitle: null,
      year: null,
      imageUrl: null,
      color: 'red',
    }),
  },
});

const leagueHex = computed(() => {
  const p = props.season?.primaryColor
  if (p && contrastOnBlack(p) >= 2) return p
  const s = props.season?.secondaryColor
  if (s && contrastOnBlack(s) >= 2) return s
  return '#00D4FF'
});

function hexLuminance(hex: string): number {
  const r = parseInt(hex.slice(1, 3), 16) / 255
  const g = parseInt(hex.slice(3, 5), 16) / 255
  const b = parseInt(hex.slice(5, 7), 16) / 255
  const toL = (c: number) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4))
  return 0.2126 * toL(r) + 0.7152 * toL(g) + 0.0722 * toL(b)
}

function contrastOnBlack(hex: string): number {
  return (hexLuminance(hex) + 0.05) / 0.05
}

// Stav pro boční menu (drawer)
const scrollY = ref(0);

const handleScroll = () => {
  scrollY.value = window.scrollY;
};

import { onMounted, onUnmounted, watch } from 'vue';

onMounted(() => {
  window.addEventListener('scroll', handleScroll);
  handleScroll(); // inicializace
  fetchLeagueAdmins();
});

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll);
});

// Season prop may arrive after mount — re-fetch when urlTitle changes
watch(() => props.season?.urlTitle, (newVal, oldVal) => {
  if (newVal && newVal !== oldVal) fetchLeagueAdmins();
});

async function fetchLeagueAdmins() {
  const urlTitle = props.season?.urlTitle;
  if (!urlTitle) return;
  try {
    const { data } = await axiosInstance.get(`${API_BASE_URL}/leagues/${urlTitle}`);
    leagueAdmins.value = data.admins || [];
  } catch {
    // silently fail — tab stays hidden
  }
}


const showStatsMenu = computed(() => route.path.includes('/leagues/') || route.path.includes('/match/') || route.path.includes('/gamecenter/'));

const activeSection = ref(0);

</script>

<style scoped>
.league-bar {
  overflow: hidden;
  border-bottom: none !important;
}

.league-bar::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: linear-gradient(to right, var(--blob-color), transparent);
  pointer-events: none;
  z-index: 1;
}

.v-tab.my-tab {
  height: 30px !important;
}

.title-section {
  max-width: 1200px;
  display: flex;
  align-items: center;
  justify-content: start;
  gap: 10px;
  text-align: center;
  padding: 3px 0;
  font-size: 0.9rem;
}

.title-text {
  font-family: 'Aileron', sans-serif;
  font-size: 1rem;
  font-weight: bold;
}

.title-wrap {
  display: flex;
  flex-direction: column;
  line-height: 1.15;
  margin-right: 8px;
}

.league-logo-link {
  cursor: pointer;
}

.league-banner-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  margin-right: 0;
}

.league-name {
  font-family: 'Barlow Condensed', 'Roboto Condensed', sans-serif;
  font-weight: 900;
  font-size: 22px;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: #ffffff;
}

.season-year {
  font-family: 'Aileron', sans-serif;
  font-size: 0.85rem;
  font-weight: bold;
  opacity: 0.55;
}

</style>
